import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdminPanelSettings, Person, Lock, Email } from "@mui/icons-material";
import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetAdminUserQuery } from "features/adminUsers/adminUsers";
import { useAppSelector } from "features/store";

export default function ClientViewAccountSettingsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const id = userId ? Number(userId) : NaN;
  const isUserManagementPanelView =
    location.pathname.endsWith("/user-management");
  const panelTitle = isUserManagementPanelView
    ? "User Management Panel (Client View)"
    : "Account Settings (Client View)";
  const disabledTooltip =
    "Client View: actions are available for this user. Open in normal mode to edit.";

  const clientView = useAppSelector((state) => state.clientView);
  const [activeSection, setActiveSection] = useState<
    "profile" | "email" | "password"
  >("profile");

  const {
    data: user,
    isLoading,
    error,
  } = useGetAdminUserQuery(id, {
    skip: !id || Number.isNaN(id),
  });

  if (!clientView.active || !clientView.userId || clientView.userId !== id) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          mx: "auto",
          p: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <Alert severity="info">Client View is not active for this user.</Alert>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 900,
          mx: "auto",
          p: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        <Alert severity="error">Failed to load client profile.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 900,
        mx: "auto",
        p: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Typography component="h1" variant="calibratePageTitle" sx={{ mb: 3 }}>
        {panelTitle}
      </Typography>

      {isMobile ? (
        <Box>
          <Tabs
            value={activeSection}
            onChange={(_, v) => setActiveSection(v)}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab value="profile" label="Profile" />
            <Tab value="email" label="Email" />
            <Tab value="password" label="Password" />
          </Tabs>
          <Box
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="calibrateSectionSubtitle"
              sx={{ mb: 2, display: "block" }}
            >
              This is a read-only view of the client's account. All fields are
              disabled in Client View.
            </Typography>
            {activeSection === "profile" && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Person
                      sx={{ fontSize: 40, color: "primary.contrastText" }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Typography variant="h6">{user.username}</Typography>
                      {user.profile?.is_superuser && (
                        <AdminPanelSettings
                          sx={{ fontSize: 20, color: "primary.main" }}
                          titleAccess="Super Admin"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {user.profile?.is_superuser
                        ? "Super Admin"
                        : user.profile?.is_calibrate_manager
                          ? "Manager"
                          : "User"}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 2, mb: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Email Address
                  </Typography>
                  <Typography variant="body1">
                    {user.email || (
                      <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                        Not set
                      </span>
                    )}
                  </Typography>
                </Box>
                {(user.profile?.is_superuser ||
                  user.profile?.is_calibrate_manager) && (
                  <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main", fontWeight: 700 }}
                    >
                      Your access & permissions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.profile?.is_superuser
                        ? "You have full system access (Super Admin)."
                        : "Read-only access to User Management. You can generate passwords for users."}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {activeSection === "email" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Email sx={{ color: "primary.main" }} />
                  <Typography variant="h6">Email</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This is how the client can change their email. In Client View
                  fields are disabled.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <TextField
                    label="Current Email"
                    value={user.email || ""}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="New Email"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <Tooltip title={disabledTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        disabled
                        fullWidth
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Update Email
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            {activeSection === "password" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Lock sx={{ color: "primary.main" }} />
                  <Typography variant="h6">Password</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This is how the client can change their password. In Client
                  View all controls are disabled.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <TextField
                    label="Current Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <Tooltip title={disabledTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        disabled
                        fullWidth
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Update Password
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            <Alert severity="info" sx={{ mt: 3 }}>
              This page mirrors what the client sees for their account settings.
              Changes cannot be made from Client View.
            </Alert>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 4 }}>
          <Box sx={{ width: 180, flexShrink: 0, pt: 1 }}>
            <Typography
              onClick={() => setActiveSection("profile")}
              sx={{
                py: 0.75,
                fontSize: 15,
                color:
                  activeSection === "profile"
                    ? "primary.main"
                    : "text.secondary",
                fontWeight: activeSection === "profile" ? 600 : 400,
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            >
              Profile
            </Typography>
            <Typography
              onClick={() => setActiveSection("email")}
              sx={{
                py: 0.75,
                fontSize: 15,
                color:
                  activeSection === "email" ? "primary.main" : "text.secondary",
                fontWeight: activeSection === "email" ? 600 : 400,
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            >
              Email
            </Typography>
            <Typography
              onClick={() => setActiveSection("password")}
              sx={{
                py: 0.75,
                fontSize: 15,
                color:
                  activeSection === "password"
                    ? "primary.main"
                    : "text.secondary",
                fontWeight: activeSection === "password" ? 600 : 400,
                cursor: "pointer",
                "&:hover": {
                  color: "primary.main",
                },
              }}
            >
              Password
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: "background.paper",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="calibrateSectionSubtitle"
              sx={{ mb: 2, display: "block" }}
            >
              This is a read-only view of the client's account. All fields are
              disabled in Client View.
            </Typography>
            {activeSection === "profile" && (
              <>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Person
                      sx={{ fontSize: 40, color: "primary.contrastText" }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <Typography variant="h6">{user.username}</Typography>
                      {user.profile?.is_superuser && (
                        <AdminPanelSettings
                          sx={{ fontSize: 20, color: "primary.main" }}
                          titleAccess="Super Admin"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {user.profile?.is_superuser
                        ? "Super Admin"
                        : user.profile?.is_calibrate_manager
                          ? "Manager"
                          : "User"}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 2, mb: 3 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Email Address
                  </Typography>
                  <Typography variant="body1">
                    {user.email || (
                      <span style={{ color: "#9ca3af", fontStyle: "italic" }}>
                        Not set
                      </span>
                    )}
                  </Typography>
                </Box>
                {(user.profile?.is_superuser ||
                  user.profile?.is_calibrate_manager) && (
                  <Box sx={{ bgcolor: "#f5f5f5", borderRadius: 1, p: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "primary.main", fontWeight: 700 }}
                    >
                      Your access & permissions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.profile?.is_superuser
                        ? "You have full system access (Super Admin)."
                        : "Read-only access to User Management. You can generate passwords for users."}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {activeSection === "email" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Email sx={{ color: "primary.main" }} />
                  <Typography variant="h6">Email</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This is how the client can change their email. In Client View
                  fields are disabled.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <TextField
                    label="Current Email"
                    value={user.email || ""}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="New Email"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <Tooltip title={disabledTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        disabled
                        fullWidth
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Update Email
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            {activeSection === "password" && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Lock sx={{ color: "primary.main" }} />
                  <Typography variant="h6">Password</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  This is how the client can change their password. In Client
                  View all controls are disabled.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <TextField
                    label="Current Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="New Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    value=""
                    size="small"
                    fullWidth
                    disabled
                  />
                  <Tooltip title={disabledTooltip}>
                    <span>
                      <Button
                        variant="contained"
                        disabled
                        fullWidth
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Update Password
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}

            <Alert severity="info" sx={{ mt: 3 }}>
              This page mirrors what the client sees for their account settings.
              Changes cannot be made from Client View.
            </Alert>
          </Box>
        </Box>
      )}
    </Box>
  );
}
